import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminConversationsAPI } from '../services/api';
import { formatDateTime } from '../utils/dateFormat';
import '../styles/AdminConversations.css';

const AdminConversations = () => {
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [search, page]);

  const fetchStats = async () => {
    try {
      const res = await adminConversationsAPI.getStats();
      setStats(res.data);
    } catch (err) {
      console.error('Erreur stats:', err);
    }
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (search) params.search = search;
      const res = await adminConversationsAPI.getAll(params);
      setConversations(res.data.conversations);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      setError(err.response?.data?.message || t('errors.loadError'));
      if (err.response?.status === 403) navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (id) => {
    try {
      setModalLoading(true);
      const res = await adminConversationsAPI.getMessages(id);
      setSelectedConversation(res.data.conversation);
      setMessages(res.data.messages);
    } catch (err) {
      setError(t('admin.conversations.loadDetailError'));
    } finally {
      setModalLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateTime(dateStr);
  };

  const truncate = (str, len) => {
    if (!str) return '-';
    return str.length > len ? str.substring(0, len) + '...' : str;
  };

  return (
    <div className="admin-conversations">
      <div className="admin-conversations-header">
        <h1>{t('admin.conversations.title')}</h1>
        <button className="btn-back" onClick={() => navigate('/admin')}>{t('common.backToDashboard')}</button>
      </div>

      {error && <div className="admin-conversations-error">{error} <button onClick={() => setError('')}>x</button></div>}

      {/* Stats bar */}
      {stats && (
        <div className="conversations-stats-bar">
          <div className="conversations-stat total">
            <span className="stat-value">{stats.total || 0}</span>
            <span className="stat-label">{t('common.total')}</span>
          </div>
          <div className="conversations-stat active-today">
            <span className="stat-value">{stats.active_today || 0}</span>
            <span className="stat-label">{t('admin.conversations.activeToday')}</span>
          </div>
          <div className="conversations-stat active-week">
            <span className="stat-value">{stats.active_7d || 0}</span>
            <span className="stat-label">{t('admin.conversations.active7d')}</span>
          </div>
          <div className="conversations-stat messages-total">
            <span className="stat-value">{stats.total_messages || 0}</span>
            <span className="stat-label">{t('admin.conversations.totalMessages')}</span>
          </div>
        </div>
      )}

      {/* Search filter */}
      <div className="conversations-filters">
        <input
          type="text"
          placeholder={t('admin.conversations.searchPlaceholder')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {loading ? (
        <div className="admin-conversations-loading">{t('common.loading')}</div>
      ) : (
        <>
          {/* Conversations table */}
          <div className="conversations-table-wrapper">
            <table className="conversations-table">
              <thead>
                <tr>
                  <th>{t('common.id')}</th>
                  <th>{t('admin.conversations.participants')}</th>
                  <th>{t('admin.conversations.product')}</th>
                  <th>{t('admin.conversations.messagesCount')}</th>
                  <th>{t('admin.conversations.lastMessage')}</th>
                  <th>{t('admin.conversations.lastActivity')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {conversations.length === 0 ? (
                  <tr><td colSpan="7" className="no-conversations">{t('admin.conversations.noConversations')}</td></tr>
                ) : (
                  conversations.map(conv => (
                    <tr key={conv.id}>
                      <td className="conv-id">#{conv.id}</td>
                      <td className="conv-participants">
                        {conv.buyer_username} <span className="participants-arrow">&harr;</span> {conv.seller_username}
                      </td>
                      <td className="conv-subject">{truncate(conv.product_title, 30)}</td>
                      <td className="conv-msg-count">{conv.message_count}</td>
                      <td className="conv-last-message">{truncate(conv.last_message, 40)}</td>
                      <td className="conv-date">{formatDate(conv.last_message_at)}</td>
                      <td className="conv-actions-cell">
                        <button className="btn-detail" onClick={() => fetchMessages(conv.id)}>{t('admin.conversations.view')}</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="conversations-pagination">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)}>{t('common.previous')}</button>
              <span>{t('common.pageOf', { current: page, total: totalPages })}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>{t('common.next')}</button>
            </div>
          )}
        </>
      )}

      {/* Conversation detail modal */}
      {(selectedConversation || modalLoading) && (
        <div className="conv-detail-overlay" onClick={() => { setSelectedConversation(null); setMessages([]); }}>
          <div className="conv-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="conv-detail-header">
              <h2>{selectedConversation ? `${t('admin.conversations.conversationNumber', { id: selectedConversation.id })}` : t('common.loading')}</h2>
              <button className="btn-close-modal" onClick={() => { setSelectedConversation(null); setMessages([]); }}>&times;</button>
            </div>

            {modalLoading ? (
              <div className="conv-detail-loading">{t('common.loading')}</div>
            ) : selectedConversation && (
              <div className="conv-detail-content">
                {/* Info section */}
                <div className="conv-info-section">
                  <div className="conv-info-grid">
                    <div className="conv-info-item">
                      <span className="conv-info-label">{t('admin.conversations.participants')}</span>
                      <span className="conv-info-value">{selectedConversation.buyer_username} &harr; {selectedConversation.seller_username}</span>
                    </div>
                    {selectedConversation.product_title && (
                      <div className="conv-info-item">
                        <span className="conv-info-label">{t('admin.conversations.product')}</span>
                        <span className="conv-info-value">{selectedConversation.product_title}</span>
                      </div>
                    )}
                    <div className="conv-info-item">
                      <span className="conv-info-label">{t('admin.conversations.createdAt')}</span>
                      <span className="conv-info-value">{formatDate(selectedConversation.created_at)}</span>
                    </div>
                    <div className="conv-info-item">
                      <span className="conv-info-label">{t('admin.conversations.lastActivity')}</span>
                      <span className="conv-info-value">{formatDate(selectedConversation.last_message_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Messages thread */}
                <div className="conv-messages-section">
                  <h3>{t('admin.conversations.messagesTitle', { count: messages.length })}</h3>
                  <div className="conv-messages-thread">
                    {messages.map(msg => (
                      <div
                        key={msg.id}
                        className={`conv-message ${msg.sender_id === selectedConversation.buyer_id ? 'msg-buyer' : 'msg-seller'}`}
                      >
                        <div className="conv-message-header">
                          <span className="conv-message-sender">{msg.sender_username}</span>
                          <span className="conv-message-date">{formatDate(msg.created_at)}</span>
                        </div>
                        <div className="conv-message-body">{msg.message}</div>
                      </div>
                    ))}
                    {messages.length === 0 && (
                      <div className="conv-no-messages">{t('admin.conversations.noMessages')}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminConversations;
